import React from 'react'
import createMeasurement from './Measurements'
import styled from 'styled-components'
import { scaleQuantile } from 'd3-scale'
import { max } from 'd3-array'
import R from 'ramda'

const RowLabel = styled.text`
  text-anchor: end;
  alignment-baseline: middle;
  stroke: #aaa;
  stroke-width: 1px;
`

const ColLabel = styled.text`
  text-anchor: middle;
  stroke: #aaa;
  stroke-width: 1px;
`

const Rect = styled.rect`
  fill: ${props => props.color};
  stroke: #e6e6e6;
  stroke-width: 1px;
`
const Legend = styled.rect`
  fill: ${props => props.color};
`
const LegendText = styled.text`
  alignment-baseline: hanging;
`
const LegendLabel = styled.text`
  text-anchor: end;
  alignment-baseline: middle;
`
const LegendBox = styled.line`
  stroke: #ccc;
  stroke-width: 1;
`

const renderLegend = ({
  colorScaleOf,
  key,
  baseY,
  legendWidth,
  legendHeight,
  valFormatter,
  label,
}) => [
  ...[0, ...colorScaleOf(key).quantiles()].map((d, i) => (
    <Legend
      key={`${key}-legend${i}`}
      x={i * legendWidth}
      y={baseY}
      width={legendWidth}
      height={legendHeight}
      color={colorScaleOf(key)(d)}
    />
  )),
  ...[0, ...colorScaleOf(key).quantiles()].map((d, i) => (
    <LegendText
      key={`${key}=legend-text${i}`}
      x={i * legendWidth}
      y={baseY + legendHeight + 3}
    >
      {`>=${valFormatter ? valFormatter(d) : Math.round(d)}`}
    </LegendText>
  )),
  label && (
    <LegendLabel
      key={`${key}=legend-label`}
      x={-10}
      y={baseY + legendHeight + 5}
    >
      {label}
    </LegendLabel>
  ),
  label && (
    <LegendBox
      key={`${key}=legend-border`}
      x1={0}
      y1={baseY + 30}
      y2={baseY + 30}
      x2={(colorScaleOf(key).quantiles().length + 1) * legendWidth}
    />
  ),
]

const createColorScaleOf = scale => key => scale.colorScale || scale[key]

const normalizeMaxDomain = maxVal => (maxVal === 0 ? 900 : maxVal)
const normalizedMax = R.compose(normalizeMaxDomain, max)

const determinColorScale = ({
  colorScale,
  colors,
  colorKey,
  rows,
  rowDataKey,
  data,
  scalePerRow,
  dataToValue,
}) =>
  scalePerRow
    ? R.compose(
        R.fromPairs,
        R.map(row => [
          row.value,
          scaleQuantile()
            .domain([
              0,
              normalizedMax(
                data.filter(d => d[rowDataKey] === row.value),
                dataToValue || (d => d[colorKey])
              ),
            ])
            .range(row.colors || colors),
        ])
      )(rows)
    : {
        colorScale: R.isNil(colorScale)
          ? scaleQuantile()
              .domain([
                0,
                normalizedMax(data, dataToValue || (d => d[colorKey])),
              ])
              .range(colors)
          : colorScale,
        scalePerRow,
      }

const NotSelected = styled.rect`
  fill: rgba(0, 0, 0, 0.5);
  pointer-events: none;
`

// only single
//
const renderSelectionEffect = ({
  isSelected,
  data,
  dataToX,
  dataToY,
  rectSize,
  rowDataKey,
  colDataKey,
  cols,
  rows,
}) => {
  if (R.isNil(isSelected)) return null
  const hasSelection = R.any(isSelected, data)
  if (!hasSelection) return null
  return data
    .filter(data => !isSelected(data))
    .map((cell, i) => (
      <NotSelected
        key={`non-select-${i}`}
        x={dataToX({ data: cell, key: colDataKey, cols, rectSize })}
        y={dataToY({ data: cell, key: rowDataKey, rows, rectSize })}
        width={rectSize}
        height={rectSize}
      />
    ))
}

const HeatMap = ({
  measurement = createMeasurement({}),
  // rowCount,
  colors,
  // colorDomain,
  cols,
  rows,
  colDataKey,
  rowDataKey,
  colorKey,
  data,
  colorScale,
  legendWidthMultiplier = 2,
  valFormatter,
  tooltipRenderer,
  scalePerRow,
  renderColLabels,
  renderRowLabels,
  squareWidth,
  legendDimension = {},
  onClick,
  isSelected,
  hideLegend,
  dataToValue, // for the color
  ...rest
}) => {
  const { margin, innerWidth } = measurement
  const rectSize = R.isNil(squareWidth)
    ? Math.floor(innerWidth / cols.length)
    : squareWidth
  const legendWidth = legendDimension.width || rectSize * legendWidthMultiplier
  const legendHeight = legendDimension.height || rectSize / 2

  const colorScaleOf = createColorScaleOf(
    determinColorScale({
      colorScale,
      colors,
      colorKey,
      rowDataKey,
      rows,
      data,
      scalePerRow,
      left: measurement.left,
      dataToValue,
    })
  )

  const toPosMap = R.compose(
    R.fromPairs,
    R.addIndex(R.map)((d, i) => [d.value, i])
  )
  const rowPosMap = toPosMap(rows)
  const colPosMap = toPosMap(cols)
  const rowOf = row => R.find(r => r.value === row)(rows)

  const dataToX =
    rest.dataToX ||
    (({ data, rectSize, key }) => colPosMap[`${data[key]}`] * rectSize)

  const dataToY =
    rest.dataToY ||
    (({ data, rectSize, key }) => rowPosMap[`${data[key]}`] * rectSize)

  return (
    <g transform={`translate(${margin.left},${margin.top})`}>
      {renderRowLabels
        ? renderRowLabels({ rows, rectSize })
        : rows.map((r, i) => (
            <RowLabel
              key={`row-l${i}`}
              x={0}
              y={i * rectSize}
              transform={`translate(${rectSize * -0.2},${rectSize / 2})`}
            >
              {r.label}
            </RowLabel>
          ))}
      {renderColLabels
        ? renderColLabels({ cols, rectSize })
        : cols.map((c, i) => (
            <ColLabel
              key={`col-l${i}`}
              x={i * rectSize}
              y={0}
              transform={`translate(${rectSize / 2},${rectSize * -0.2})`}
            >
              {c.label}
            </ColLabel>
          ))}
      {data.map((d, i) => (
        <Rect
          x={dataToX({ data: d, cols, rectSize, key: colDataKey })}
          y={dataToY({ data: d, rows, rectSize, key: rowDataKey })}
          width={rectSize}
          height={rectSize}
          color={colorScaleOf(d[rowDataKey])(
            dataToValue ? dataToValue(d) : d[colorKey]
          )}
          key={`rect-${i}`}
          selected={isSelected && isSelected(d)}
          onClick={onClick && (() => onClick(d))}
        >
          <title>
            {tooltipRenderer &&
              tooltipRenderer(
                rowOf(d[rowDataKey]).formatter || valFormatter
                  ? {
                      ...d,
                      value: (rowOf(d[rowDataKey]).formatter || valFormatter)(
                        d.value
                      ),
                      row: rowOf(d[rowDataKey]),
                    }
                  : d
              )}
          </title>
        </Rect>
      ))}
      {renderSelectionEffect({
        data,
        dataToX,
        dataToY,
        isSelected,
        rectSize,
        rowDataKey,
        colDataKey,
        cols,
        rows,
      })}
      {!scalePerRow &&
        !hideLegend &&
        renderLegend({
          colorScaleOf,
          key: 'legend',
          baseY: rows.length * rectSize + 20,
          legendWidth,
          legendHeight,
          valFormatter,
        })}
      {scalePerRow &&
        !hideLegend &&
        rows.map((row, i) =>
          renderLegend({
            colorScaleOf,
            key: row.value,
            label: row.label,
            baseY: rows.length * rectSize + 30 + i * 35,
            legendWidth,
            legendHeight,
            valFormatter: row.formatter || valFormatter,
            left: measurement.margin.left,
          })
        )}
    </g>
  )
}

export default HeatMap
